'use client';

import { useCallback } from 'react';
import useVelocityClient from 'src/hooks/useVelocityClient';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { notify } from 'src/utils/notifications';
import TransactionErrorHandler, {
	tryGetVelocityErrorCode,
} from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import { useWaitForTxConfirm } from '../transactions/useWaitForTxConfirm';
import { Adapter } from '@solana/wallet-adapter-base';

/**
 * Hook for reclaiming excess rent from closed accounts
 * NOTE: we cannot use a non-authority fee payer to execute reclaiming of rent, due to a program bug. however, because this "feature" isn't used anymore, we can just use the default fee payer.
 */
export const useReclaimRent = () => {
	const velocityClient = useVelocityClient();
	const appEventEmitter = useVelocityStore((s) => s.appEventEmitter);
	const walletAdapter = useVelocityStore((s) => s.wallet?.current?.adapter);
	const { waitForTxConfirm } = useWaitForTxConfirm();

	const reclaimRent = useCallback(
		async (userId: number) => {
			if (!velocityClient) return;

			const toastId = `reclaimRent_${userId}`;

			try {
				notify({
					type: 'awaiting_wallet_confirmation',
					id: toastId,
					showUntilCancelled: true,
				});

				const txSig = await velocityClient.reclaimRent(userId);

				await waitForTxConfirm(txSig as string);

				notify({
					type: 'success',
					message: 'Excess rent reclaimed.',
					id: toastId,
					showUntilCancelled: false,
				});

				appEventEmitter.emit('claimedExcessRent');
			} catch (e: any) {
				const velocityError = tryGetVelocityErrorCode(e);

				if (velocityError && velocityError.name === 'CantReclaimRent') {
					notify({
						type: 'success',
						message: 'No excess rent to claim.',
						description:
							'There is no excess rent in this account to claim. If you want to claim the full amount of rent, you will need to delete the subaccount.',
						id: toastId,
						showUntilCancelled: false,
					});
				} else {
					TransactionErrorHandler.handleError({
						error: e,
						toastId,
						wallet: walletAdapter as Adapter,
					});
				}
			}
		},
		[velocityClient, appEventEmitter, waitForTxConfirm, walletAdapter]
	);

	return { reclaimRent };
};
